/**
 * QuoteCart — gestiona la cotización en localStorage y el drawer de cotización.
 */

const STORAGE_KEY = 'ofq_quote_cart';

const QuoteCart = {
  getItems() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  },

  save(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('quote:updated', { detail: { items } }));
  },

  addItem(item) {
    const items = this.getItems();
    const existing = items.find(i => i.variantId === item.variantId);
    if (existing) {
      existing.quantity += item.quantity || 1;
    } else {
      items.push({ ...item, quantity: item.quantity || 1 });
    }
    this.save(items);
  },

  removeItem(variantId) {
    const items = this.getItems().filter(i => i.variantId !== variantId);
    this.save(items);
  },

  updateQuantity(variantId, qty) {
    const items = this.getItems();
    const item = items.find(i => i.variantId === variantId);
    if (item) {
      item.quantity = Math.max(1, parseInt(qty) || 1);
      this.save(items);
    }
  },

  clear() {
    localStorage.removeItem(STORAGE_KEY);
    document.dispatchEvent(new CustomEvent('quote:updated', { detail: { items: [] } }));
  },

  getCount() {
    return this.getItems().reduce((sum, i) => sum + i.quantity, 0);
  },

  formatMoney(cents) {
    return (cents / 100).toLocaleString('es-CL', { style: 'currency', currency: 'CLP' });
  }
};

/* ── Drawer ──────────────────────────────────────────────────────── */

class QuoteDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = document.getElementById('quote-drawer-overlay');
  }

  connectedCallback() {
    document.addEventListener('quote:updated', () => this.render());
    this.addEventListener('click', e => {
      if (e.target.closest('.quote-drawer__close') || e.target === this.overlay) this.close();
      if (e.target.closest('[data-remove]')) {
        QuoteCart.removeItem(parseInt(e.target.closest('[data-remove]').dataset.remove));
      }
      if (e.target.closest('[data-qty-change]')) {
        const btn = e.target.closest('[data-qty-change]');
        const row = btn.closest('[data-variant]');
        const variantId = parseInt(row.dataset.variant);
        const delta = parseInt(btn.dataset.qtyChange);
        const items = QuoteCart.getItems();
        const item = items.find(i => i.variantId === variantId);
        if (item) {
          const newQty = item.quantity + delta;
          if (newQty < 1) QuoteCart.removeItem(variantId);
          else QuoteCart.updateQuantity(variantId, newQty);
        }
      }
    });
    this.render();
  }

  open() {
    this.setAttribute('open', '');
    this.overlay && this.overlay.classList.add('active');
    document.body.classList.add('overflow-hidden');
    this.render();
  }

  close() {
    this.removeAttribute('open');
    this.overlay && this.overlay.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  render() {
    const items = QuoteCart.getItems();
    const list = this.querySelector('.quote-drawer__items');
    const empty = this.querySelector('.quote-drawer__empty');
    const footer = this.querySelector('.quote-drawer__footer');
    const badge = document.querySelector('.quote-count-bubble');
    const count = QuoteCart.getCount();

    if (badge) {
      badge.style.display = count > 0 ? '' : 'none';
      badge.querySelector('span') && (badge.querySelector('span').textContent = count);
    }

    if (!list) return;

    if (items.length === 0) {
      list.innerHTML = '';
      empty && (empty.hidden = false);
      footer && (footer.hidden = true);
      return;
    }

    empty && (empty.hidden = true);
    footer && (footer.hidden = false);

    list.innerHTML = items.map(item => `
      <div class="quote-drawer__item" data-variant="${item.variantId}">
        <div class="quote-drawer__item-image">
          ${item.image
            ? `<img src="${item.image}" alt="${item.productTitle}" width="64" height="64" loading="lazy">`
            : `<div class="quote-drawer__item-image-placeholder"></div>`}
        </div>
        <div class="quote-drawer__item-info">
          <p class="quote-drawer__item-title">${item.productTitle}</p>
          ${item.variantTitle && item.variantTitle !== 'Default Title'
            ? `<p class="quote-drawer__item-variant">${item.variantTitle}</p>` : ''}
          <p class="quote-drawer__item-price">${QuoteCart.formatMoney(item.price)}</p>
          <div class="quote-drawer__qty">
            <button class="quote-drawer__qty-btn" data-qty-change="-1" aria-label="Disminuir">−</button>
            <span class="quote-drawer__qty-num">${item.quantity}</span>
            <button class="quote-drawer__qty-btn" data-qty-change="1" aria-label="Aumentar">+</button>
          </div>
        </div>
        <button class="quote-drawer__remove" data-remove="${item.variantId}" aria-label="Eliminar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `).join('');
  }
}

customElements.define('quote-drawer', QuoteDrawer);

/* ── Botón "Agregar a cotización" ──────────────────────────────── */

document.addEventListener('click', e => {
  const btn = e.target.closest('.quote-add-btn');
  if (!btn) return;
  e.preventDefault();

  // Leer variante seleccionada del formulario del producto
  const form = btn.closest('[data-type="add-to-cart-form"]') || document.querySelector('[data-type="add-to-cart-form"]');
  const variantInput = form ? form.querySelector('.product-variant-id') : null;
  const variantId = variantInput ? parseInt(variantInput.value) : parseInt(btn.dataset.variantId);

  if (!variantId) return;

  QuoteCart.addItem({
    variantId,
    sku: btn.dataset.sku || '',
    productTitle: btn.dataset.productTitle || '',
    variantTitle: btn.dataset.variantTitle || '',
    price: parseInt(btn.dataset.price) || 0,
    image: btn.dataset.image || '',
    url: btn.dataset.url || ''
  });

  // Notificación rápida
  const label = btn.querySelector('.quote-btn-label');
  if (label) {
    const original = label.textContent;
    label.textContent = '✓ Agregado';
    setTimeout(() => { label.textContent = original; }, 1800);
  }

  // Abrir drawer
  const drawer = document.querySelector('quote-drawer');
  if (drawer) drawer.open();
});

/* ── Ícono del header ──────────────────────────────────────────── */

document.addEventListener('click', e => {
  if (e.target.closest('#quote-icon-bubble')) {
    e.preventDefault();
    const drawer = document.querySelector('quote-drawer');
    if (drawer) {
      drawer.hasAttribute('open') ? drawer.close() : drawer.open();
    }
  }
});

/* ── Página de cotización: prellenado desde localStorage ─────── */

document.addEventListener('DOMContentLoaded', () => {
  const form = document.getElementById('quote-contact-form');
  if (!form) return;

  const items = QuoteCart.getItems();
  const hiddenItems = form.querySelector('#quote-items-hidden');
  const preview = document.getElementById('quote-items-preview');

  if (!items.length) {
    if (preview) preview.innerHTML = '<p style="color:#888">No hay productos en la cotización.</p>';
    return;
  }

  const lines = items.map(i =>
    `${i.productTitle}${i.variantTitle && i.variantTitle !== 'Default Title' ? ' — ' + i.variantTitle : ''} | Cantidad: ${i.quantity} | Precio unitario: ${QuoteCart.formatMoney(i.price)}`
  ).join('\n');

  if (hiddenItems) hiddenItems.value = lines;

  if (preview) {
    preview.innerHTML = `
      <table class="quote-preview-table">
        <thead><tr><th>Producto</th><th>Variante</th><th>Cantidad</th><th>Precio unit.</th></tr></thead>
        <tbody>
          ${items.map(i => `
            <tr>
              <td>${i.productTitle}</td>
              <td>${i.variantTitle && i.variantTitle !== 'Default Title' ? i.variantTitle : '—'}</td>
              <td>${i.quantity}</td>
              <td>${QuoteCart.formatMoney(i.price)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>`;
  }

  form.addEventListener('submit', () => {

    var datos = {
      nombre:        document.getElementById('quote-name')?.value        || '',
      email:         document.getElementById('quote-email')?.value       || '',
      telefono:      document.getElementById('quote-phone')?.value       || '',
      razon_social:  document.getElementById('quote-company')?.value     || '',
      rut:           document.getElementById('quote-rut')?.value         || '',
      giro:          document.getElementById('quote-giro')?.value        || '',
      uso_final:     document.getElementById('quote-uso-final')?.value   || '',
      observaciones: document.getElementById('quote-notes')?.value       || '',
      direccion:     document.getElementById('quote-address1')?.value    || '',
      adicional:     document.getElementById('quote-address2')?.value    || '',
      comuna:        document.getElementById('quote-comuna')?.value      || '',
      ciudad:        document.getElementById('quote-ciudad')?.value      || '',
      codigo_postal: document.getElementById('quote-postal')?.value      || '',
      fecha:         new Date().toLocaleDateString('es-CL'),
      productos: QuoteCart.getItems().map(function(item) {
        var precioUnitario = Math.round(item.price / 100);
        var totalLinea = precioUnitario * item.quantity;
        return {
          sku:            item.sku || '',
          titulo:         item.productTitle,
          variante:       (item.variantTitle && item.variantTitle !== 'Default Title')
                            ? item.variantTitle : '',
          cantidad:       item.quantity,
          precio_neto:    precioUnitario,
          total_linea:    totalLinea
        };
      }),
      subtotal_neto: (function() {
        var items = QuoteCart.getItems();
        var neto = 0;
        items.forEach(function(item) {
          neto += Math.round(item.price / 100) * item.quantity;
        });
        return neto;
      })(),
      iva: (function() {
        var items = QuoteCart.getItems();
        var neto = 0;
        items.forEach(function(item) {
          neto += Math.round(item.price / 100) * item.quantity;
        });
        return Math.round(neto * 0.19);
      })(),
      total: (function() {
        var items = QuoteCart.getItems();
        var neto = 0;
        items.forEach(function(item) {
          neto += Math.round(item.price / 100) * item.quantity;
        });
        return Math.round(neto * 1.19);
      })()
    };

    fetch('https://prod-02.brazilsouth.logic.azure.com:443/workflows/2dee81a232d24307984ee35236e8341e/triggers/When_an_HTTP_request_is_received/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2FWhen_an_HTTP_request_is_received%2Frun&sv=1.0&sig=yoJ_XG_Z2TvHtrZ-epXMDPIe9g0d-I7n69lJ-9s_aGA', {
      method:    'POST',
      mode:      'no-cors',
      body:      JSON.stringify(datos),
      keepalive: true
    });

    setTimeout(function() { QuoteCart.clear(); }, 500);
  });
});
